import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useLocation, useNavigation } from 'react-router-dom';
import {
    Paper,
    Typography,
    Button,
    Box,
    TextField,
    Tabs,
    Tab,
    Grid2,
    IconButton,
    InputAdornment,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { backArrow } from "../../assets/Icons";

import { useDispatch, useSelector } from "react-redux";
import { getDeviceList } from "../../redux/reducers/deviceReducer";
import CustomGenericTable from "../../components/CustomGenericTable";
import { getPatientList } from "../../redux/reducers/patientReducer";
import Loader from "../../components/Loader";
import moment from "moment";
import withNavigate from "../../hocs/withNavigate";
import withToast from "../../hocs/withToast";

// Column definitions for the petient table
const columns = [
    { id: "patientId", label: "ID" },
    { id: "patientName", label: "Patient Name" },
    { id: "birthDay", label: "DOB" },
    { id: "gender", label: "Gender" },
    { id: "serialNumber", label: "Device" },
    { id: "heartRate", label: "HR" },
    { id: "bloodGlucose", label: "Glucose" },
    { id: "respirationRate", label: "Respiration Rate" },
    { id: "bloodPressure", label: "BP" },
    { id: "spo2", label: "SPO2" },
    { id: "lastTimeActivity", label: "Last Sync" },
];

// Utility function to get vital value from device data
const getVitalValue = (deviceData, vitalType) => {
  try {
    const parsedData = JSON.parse(deviceData || '{}');
    return parsedData[vitalType];
  } catch (error) {
    console.error('Failed to parse device data:', error);
    return null;
  }
};

const PatientList = ({ navigate, notify }) => {
    const dispatch = useDispatch();

    const { patientList = [], loading, rowCount = 0 } = useSelector((state) => state?.patient);
    const [patient, setPatients] = useState(patientList);
    const [filteredPatient, setFilterPatient] = useState(patient || []);
    const [searchQuery, setSearchQuery] = useState("");

    const filteredPatients = useMemo(() => {
        if (!searchQuery) return patient;
        
        const lowerQuery = searchQuery.toLowerCase();
        return patient.filter((patient) =>
            patient.patientFirstName?.toLowerCase().includes(lowerQuery) ||
            patient.patientLastName?.toLowerCase().includes(lowerQuery)
        );
    }, [searchQuery, patient]);

    useEffect(() => {
        setPatients(patientList);
    }, [patientList]);

    useEffect(() => {
        dispatch(getPatientList());
    }, [dispatch]);

    useEffect(() => {
        setFilterPatient(filteredPatients);
    }, [filteredPatients]);

    const handlePatientClick = useCallback((patientId) => {
        navigate("/patient-chart", {
            state: { patientId },
        });
    }, [navigate]);

    const validatePatientClick = useCallback((e) => {
        if(!e?.serialNumber)
            return notify('No watch connected with Patient', 'error');
        handlePatientClick(e.patientId);
    }, [handlePatientClick, notify]);

    const rowsWithAction = useCallback((rows) => {
        if (!rows?.length) return [];
        return rows.map((e) => ({
            ...e,
            patientName: `${e.patientFirstName} ${e.patientLastName}`,
            birthDay: e.birthDay ? moment(e.birthDay).format('DD-MM-YYYY') : '',
            lastTimeActivity: e.lastTimeActivity ? moment(e.lastTimeActivity).format('DD-MM-YYYY, h:mm:ss a') : '',
            heartRate: getVitalValue(e?.lastDeviceData, 'HR'),
            bloodGlucose: getVitalValue(e?.lastDeviceData, 'Glucose'),
            spo2: getVitalValue(e?.lastDeviceData, 'SPO2'),
            bloodPressure: `${JSON.parse(e?.lastDeviceData)?.SBP}/${JSON.parse(e?.lastDeviceData)?.DBP}`,
            respirationRate: getVitalValue(e?.lastDeviceData, 'RR')
        }));
    }, [validatePatientClick]);

    return (
        <>
            {/* Header Section */}
            <Grid2
                container
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
                sx={{ flexWrap: { xs: "wrap", md: "nowrap" }, p: { xs: "1", sm: "2" } }}
            >
                <Grid2
                    item
                    xs={12}
                    md={6}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "nowrap",
                        justifyContent: { xs: "center", sm: "flex-start" },
                    }}
                >
                    {/* Back button and title */}
                    <IconButton sx={{ color: "black" }} onClick={() => navigate('/patient-population')}>
                        <img src={backArrow} alt="Back" width={24} height={24} />
                    </IconButton>
                    <Typography
                        variant="h4"
                        fontWeight="bold"
                        sx={{ fontSize: { xs: "1.3rem", sm: "1.5rem", md: "2rem" } }}
                    >
                        Patient List
                    </Typography>
                </Grid2>
            </Grid2>

            {/* Device Table Section */}
            <Box
                sx={{
                    bgcolor: "#f5f5f5",
                    mt: 3,
                    border: "1px solid #ddd",
                    borderRadius: "10px",
                }}
            >
                {/* Search and Tabs for filtering */}
                <Grid2
                    container
                    spacing={2}
                    alignItems="center"
                    sx={{ my: 0, borderRadius: "10px", p: 2, bgcolor: "white" }}
                >
                    {/* Search field */}
                    <Grid2
                        item
                        xs={12}
                        md={6}
                        sx={{ display: "flex", alignItems: "center" }}
                    >
                        <TextField
                            fullWidth
                            sx={{
                                width: { xs: "100%", md: "600px" },
                                maxWidth: "750px",
                                flexGrow: 1,
                                borderRadius: "50px",
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "50px",
                                },
                            }}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search by Patient Name"
                            variant="outlined"
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconButton edge="start">
                                            <Search sx={{ color: "#c1c5cd" }} />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid2>
                </Grid2>

                {/* Displaying the device tables */}
                <CustomGenericTable
                    title="CONNECTED"
                    columns={columns}
                    rows={rowsWithAction(filteredPatient) || []}
                    rowCount={rowCount}
                    handleNameClickCb={validatePatientClick}
                />
            </Box>
            <Loader loading={loading} />
        </>
    );
};

export default withToast(withNavigate(PatientList));
